import { useNavigate } from 'react-router-dom';
import { usePredictionMarketSearch, usePredictionMarketEventsFindLobby } from '@/lib/endpoints';
import { TRENDING_CATEGORY_CODE, useAppSearchParams } from '@/hooks/useAppSearchParams';
import { Skeleton } from '@/components/ui';
import { SearchRow } from './SearchRow';

const SKELETON_COUNT = 6;
const RELATED_LIMIT = 6;
const WRAPPER = 'w-full px-3.5 md:px-0 pb-6 flex flex-col gap-6';
const LIST = 'flex flex-col gap-2';

export interface SearchResultsFeedProps {
  /** Already-debounced query string. Empty → nothing rendered (entry state). */
  query: string;
  /** Called with the query when a result is opened, so the page can record it. */
  onResultOpen?: (query: string) => void;
}

function RowSkeletons({ count }: { count: number }) {
  return (
    <div className={LIST}>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} className="h-[56px] w-full rounded-[12px]" />
      ))}
    </div>
  );
}

/**
 * Live search results (ARC-113 follow-up). One {@link SearchRow} per matched
 * event — the query bolded in the title — that taps through to the event page.
 * Search always returns events (grouped by event); markets are never listed
 * here. Below the results, an unfiltered "Related" list (lobby feed) suggests
 * more events. Empty input renders nothing; the page shows its entry state.
 */
export function SearchResultsFeed({ query, onResultOpen }: SearchResultsFeedProps) {
  const navigate = useNavigate();
  const trimmed = query.trim();
  const enabled = trimmed.length > 0;

  const { categoryCode } = useAppSearchParams();
  const categoryFilter = categoryCode !== TRENDING_CATEGORY_CODE ? categoryCode : undefined;

  const { data, isLoading, isError } = usePredictionMarketSearch(
    { q: trimmed, categoryCode: categoryFilter },
    { query: { enabled } },
  );

  // "Related" is shown only for the unfiltered result view (per design).
  const showRelated = enabled && !categoryFilter;
  const { data: relatedData } = usePredictionMarketEventsFindLobby(
    { limit: RELATED_LIMIT },
    { query: { enabled: showRelated } },
  );

  const events = data?.events ?? [];
  const related = relatedData?.events ?? [];

  function openEvent(eventId: string) {
    onResultOpen?.(trimmed);
    navigate(`/events/${eventId}`);
  }

  // No query yet → the page shows its entry state, not this feed.
  if (!enabled) return null;

  if (isError) {
    return <div className="w-full px-3.5 md:px-0 py-10 text-center text-sm text-muted">Search failed. Try again.</div>;
  }

  if (isLoading) {
    return (
      <div className={WRAPPER}>
        <RowSkeletons count={SKELETON_COUNT} />
      </div>
    );
  }

  if (events.length === 0) {
    return <div className="w-full px-3.5 md:px-0 py-16 text-center text-sm text-muted">No results found</div>;
  }

  return (
    <div className={WRAPPER}>
      <div className={`${LIST} search-stagger`}>
        {events.map((event) => (
          <SearchRow
            key={event.id}
            label={event.name}
            highlight={trimmed}
            onClick={() => openEvent(event.id)}
            data-testid="search-result-row"
          />
        ))}
      </div>

      {showRelated && related.length > 0 && (
        <section className="flex flex-col gap-3.5">
          <h2 className="m-0 text-[18px] font-medium text-ink leading-normal">Related</h2>
          <div className={LIST}>
            {related.map((event) => (
              <SearchRow
                key={event.id}
                label={event.name}
                onClick={() => navigate(`/events/${event.id}`)}
                data-testid="search-related-row"
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
